import { useEffect, useState } from "react";
import { ArrowLeft, Pencil } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui";
import { getWorkflowDraft } from "./api";
import type { WorkflowDraft } from "./models";
import { useBuilderIdentity } from "./useBuilderIdentity";
import { WorkflowBuilderCanvas } from "./WorkflowBuilderCanvas";
import { ReminderStatus, ReminderTaskCard } from "./ReminderTaskCard";
import { useReminderStrings } from "./reminderStrings";
import browserStyles from "../../components/NoteWorkflows/NoteWorkflows.module.css";
import styles from "./WorkflowBuilder.module.css";

type Props = {
    draftId: string;
    organizationId?: string;
    switching: boolean;
    onBack: () => void;
    onEdit: () => void;
};

export function WorkflowDraftDetails({ draftId, organizationId, switching, onBack, onEdit }: Props) {
    const { t } = useTranslation();
    const reminderWords = useReminderStrings();
    const { resolveToken } = useBuilderIdentity();
    const [draft, setDraft] = useState<WorkflowDraft>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        const controller = new AbortController();
        setDraft(undefined);
        setLoading(true);
        setError("");
        if (!switching) void (async () => {
            try {
                const token = await resolveToken();
                if (controller.signal.aborted) return;
                const result = await getWorkflowDraft(draftId, token, controller.signal, organizationId);
                if (!controller.signal.aborted) setDraft(result);
            } catch (caught: unknown) {
                if (!controller.signal.aborted) setError(caught instanceof Error ? caught.message : t("workflowBuilder.loadError"));
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        })();
        return () => controller.abort();
    }, [draftId, organizationId, resolveToken, switching, attempt, t]);

    const title = draft?.workflow.title.trim() || t("settingsPage.company.noteWorkflows.unnamedWorkflow", {
        defaultValue: "Untitled workflow · {{id}}", id: draftId
    });

    return <section className={browserStyles.workflowBrowserDetail} aria-label={t("workflowBuilder.boardTitle")} aria-busy={loading || switching}>
        <header className={browserStyles.workflowBrowserToolbar}>
            <button type="button" className={browserStyles.workflowBrowserBack} onClick={onBack}>
                <ArrowLeft size={16} aria-hidden="true" />
                {t("settingsPage.company.noteWorkflows.allWorkflows", { defaultValue: "All workflows" })}
            </button>
            {draft && !loading && !switching && <h3>{title}</h3>}
            <div className={browserStyles.workflowBrowserActions}>
                <button type="button" className={browserStyles.editButton} disabled={loading || switching || !draft || Boolean(error)} onClick={onEdit}>
                    <Pencil size={16} aria-hidden="true" />{t("settingsPage.account.edit")}
                </button>
            </div>
        </header>
        {(loading || switching) && <p role="status" className={styles.working}>{t("workflowBuilder.loading")}</p>}
        {error && <div role="alert" className={styles.error}>
            <p>{error}</p><Button disabled={switching} onClick={() => setAttempt(value => value + 1)}>{t("workflowBuilder.retry")}</Button>
        </div>}
        {draft && !loading && !switching && !error && (draft.workflow.kind === "reminder" ? <ReminderTaskCard draft={draft}
            firstSaveHelp={organizationId ? t("organizationWorkflowBuilder.scheduledHelp") : undefined} /> : <WorkflowBuilderCanvas workflow={draft.workflow} draftId={draft.id} variant="detail" />)}
        {draft && draft.workflow.kind !== "reminder" && draft.schedule_state && !loading && !switching && !error && <p className={styles.previewHint}><ReminderStatus state={draft.schedule_state} /> {reminderWords.activeOtherKind}</p>}
    </section>;
}
